import {
  collection,
  query,
  where,
  addDoc,
  getDocs,
  serverTimestamp,
  orderBy,
  limit,
  updateDoc,
  doc
} from 'firebase/firestore';
import { db } from './firebase';
import { createMentionNotification } from './notificationService';

// Collection references
const mentionsRef = collection(db, 'mentions');

// Regular expression to detect @mentions in text
export const MENTION_REGEX = /@([a-zA-Z0-9_]{3,15})/g;

// Create a new mention
export const createMention = async (commentId, mentionedUserId, createdByUserId, questionId) => {
  try {
    const mention = {
      commentId,
      mentionedUserId,
      createdByUserId,
      questionId,
      createdAt: serverTimestamp(),
      isRead: false
    };
    
    const docRef = await addDoc(mentionsRef, mention);
    return {
      id: docRef.id,
      ...mention
    };
  } catch (error) {
    console.error('Error creating mention:', error);
    throw error;
  }
};

// Get mentions for a user
export const getMentionsForUser = async (userId, unreadOnly = false, page = 1, pageSize = 10) => {
  try {
    if (!userId) return { mentions: [], hasMore: false };
    
    let mentionsQuery;
    
    if (unreadOnly) {
      mentionsQuery = query(
        mentionsRef,
        where('mentionedUserId', '==', userId),
        where('isRead', '==', false),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
    } else {
      mentionsQuery = query(
        mentionsRef,
        where('mentionedUserId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
    }
    
    const mentionsSnapshot = await getDocs(mentionsQuery);
    
    if (mentionsSnapshot.empty) {
      return { mentions: [], hasMore: false };
    }
    
    const mentions = [];
    mentionsSnapshot.forEach(doc => {
      mentions.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return {
      mentions,
      hasMore: mentions.length === pageSize,
      page
    };
  } catch (error) {
    console.error('Error getting mentions:', error);
    throw error;
  }
};

// Mark a mention as read
export const markMentionAsRead = async (mentionId) => {
  try {
    const mentionRef = doc(db, 'mentions', mentionId);
    await updateDoc(mentionRef, {
      isRead: true
    });
    return true;
  } catch (error) {
    console.error('Error marking mention as read:', error);
    throw error;
  }
};

// Extract mentions from text
export const extractMentionsFromText = (text) => {
  if (!text) return [];
  
  // This regex matches our stored [@username] format
  const mentionRegex = /\[@(\w+)\]/g;
  const mentions = [];
  let match;
  
  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push(match[1]); // Extract just the username
  }
  
  return mentions;
};

// Process mentions in a comment
export const processMentionsInComment = async (commentId, commentText, createdByUserId, questionId) => {
  try {
    const mentions = extractMentionsFromText(commentText);
    
    // If no mentions, do nothing
    if (mentions.length === 0) return [];
    
    const processedMentions = [];
    
    // For each mentioned username, find the user and create a mention
    for (const username of mentions) {
      // Simple approach - find user by username
      // In a real implementation, you'd want a more efficient way to lookup users by username
      const usersRef = collection(db, 'users');
      const userQuery = query(
        usersRef,
        where('username', '==', username)
      );
      
      const userSnapshot = await getDocs(userQuery);
      
      if (!userSnapshot.empty) {
        userSnapshot.forEach(doc => {
          const userId = doc.id;
          
          // Don't create mentions for the comment author
          if (userId !== createdByUserId) {
            createMention(commentId, userId, createdByUserId, questionId)
              .then(mention => {
                processedMentions.push(mention);
              })
              .catch(error => {
                console.error('Error creating mention for user:', username, error);
              });
          }
        });
      }
    }
    
    return processedMentions;
  } catch (error) {
    console.error('Error processing mentions:', error);
    return [];
  }
};

// ========================================
// ENHANCED TAGGING SYSTEM FUNCTIONS
// ========================================

// Parse text and extract @mentions with positions
export const parseMentions = (text) => {
  const mentions = [];
  let match;
  
  // Reset regex lastIndex to ensure fresh parsing
  MENTION_REGEX.lastIndex = 0;
  
  while ((match = MENTION_REGEX.exec(text)) !== null) {
    const username = match[1];
    const startIndex = match.index;
    const endIndex = match.index + match[0].length;
    
    mentions.push({
      username,
      startIndex,
      endIndex,
      fullMatch: match[0]
    });
  }
  
  return mentions;
};

// Search for users by username for autocomplete
export const searchUsersByUsername = async (searchTerm, maxResults = 10) => {
  try {
    if (!searchTerm || searchTerm.length < 1) {
      return [];
    }
    
    // Clean search term
    const cleanTerm = searchTerm.toLowerCase().trim();
    
    // Query users collection for usernames starting with search term
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('username', '>=', cleanTerm),
      where('username', '<=', cleanTerm + '\uf8ff'),
      orderBy('username'),
      limit(maxResults)
    );
    
    const querySnapshot = await getDocs(q);
    const users = [];
    
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      if (userData.username) {
        users.push({
          uid: doc.id,
          username: userData.username,
          displayName: userData.displayName || userData.username,
          photoURL: userData.photoURL || null
        });
      }
    });
    
    return users;
  } catch (error) {
    console.error('Error searching users:', error);
    return [];
  }
};

// Validate if mentioned usernames exist
export const validateMentions = async (mentions) => {
  try {
    if (!mentions || mentions.length === 0) {
      return [];
    }
    
    const usernames = mentions.map(m => m.username.toLowerCase());
    const usersRef = collection(db, 'users');
    
    // Query for all mentioned usernames
    const q = query(
      usersRef,
      where('username', 'in', usernames)
    );
    
    const querySnapshot = await getDocs(q);
    const validUsers = [];
    
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      if (userData.username) {
        validUsers.push({
          uid: doc.id,
          username: userData.username,
          displayName: userData.displayName || userData.username
        });
      }
    });
    
    // Return only mentions with valid users
    return mentions.filter(mention => 
      validUsers.some(user => 
        user.username.toLowerCase() === mention.username.toLowerCase()
      )
    ).map(mention => {
      const user = validUsers.find(u => 
        u.username.toLowerCase() === mention.username.toLowerCase()
      );
      return {
        ...mention,
        user
      };
    });
  } catch (error) {
    console.error('Error validating mentions:', error);
    return [];
  }
};

// React component helper to render mentions in JSX
export const renderMentionsAsJSX = (text, onMentionClick) => {
  if (!text) return text;
  
  const parts = [];
  let lastIndex = 0;
  let match;
  
  // Reset regex lastIndex
  MENTION_REGEX.lastIndex = 0;
  
  while ((match = MENTION_REGEX.exec(text)) !== null) {
    const username = match[1];
    const startIndex = match.index;
    
    // Add text before mention
    if (startIndex > lastIndex) {
      parts.push(text.slice(lastIndex, startIndex));
    }
    
    // Add mention as clickable element
    parts.push({
      type: 'mention',
      username,
      text: match[0],
      key: `mention-${startIndex}-${username}`
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  return parts.map((part, index) => {
    if (typeof part === 'string') {
      return part;
    }
    
    if (part.type === 'mention') {
      return (
        <span
          key={part.key || `mention-${index}`}
          className="mention-link"
          onClick={() => onMentionClick && onMentionClick(part.username)}
          data-username={part.username}
        >
          {part.text}
        </span>
      );
    }
    
    return part;
  });
};

// Send notifications to mentioned users
export const notifyMentionedUsers = async (mentions, fromUser, context) => {
  try {
    if (!mentions || mentions.length === 0) return;
    
    for (const mention of mentions) {
      if (mention.user && mention.user.uid !== fromUser.uid) {
        await createMentionNotification(
          mention.user.uid,
          fromUser,
          context.type,
          context.id,
          context.title || context.text
        );
      }
    }
  } catch (error) {
    console.error('Error sending mention notifications:', error);
  }
};

// Get mention context in text for autocomplete positioning
export const getMentionContext = (text, cursorPosition) => {
  if (!text || cursorPosition < 0) {
    return null;
  }
  
  // Find the last @ before cursor position
  const textBeforeCursor = text.slice(0, cursorPosition);
  const lastAtIndex = textBeforeCursor.lastIndexOf('@');
  
  if (lastAtIndex === -1) {
    return null;
  }
  
  // Check if there's a space or end of string after @
  const textAfterAt = text.slice(lastAtIndex + 1, cursorPosition);
  
  // Must be a valid username pattern (no spaces)
  if (/\s/.test(textAfterAt)) {
    return null;
  }
  
  return {
    startIndex: lastAtIndex,
    searchTerm: textAfterAt,
    isActive: true
  };
};

// Insert mention into text at specific position
export const insertMention = (text, mention, startIndex, endIndex) => {
  const beforeText = text.slice(0, startIndex);
  const afterText = text.slice(endIndex);
  const mentionText = `@${mention.username}`;
  
  return {
    newText: beforeText + mentionText + ' ' + afterText,
    newCursorPosition: beforeText.length + mentionText.length + 1
  };
};

// Extract mentioned usernames from processed text
export const extractMentionedUsernames = (text) => {
  const mentions = parseMentions(text);
  return [...new Set(mentions.map(m => m.username))];
};

// Get user by username for mention validation
export const getUserByUsername = async (username) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('username', '==', username.toLowerCase()),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const userData = doc.data();
      return {
        uid: doc.id,
        username: userData.username,
        displayName: userData.displayName || userData.username,
        photoURL: userData.photoURL || null
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user by username:', error);
    return null;
  }
};